import { useState } from 'react';
import { ChevronDown, ChevronUp, Search, X } from 'lucide-react';
import type { OversightFilters as OversightFiltersType, ReviewStatus } from '../../types';
import { fieldCls } from '../../../ui';

interface Site {
  id: string;
  name: string;
}

interface Protocol {
  id: string;
  protocol_number: string;
}

interface OversightFiltersProps {
  filters: OversightFiltersType;
  onFilterChange: (filters: OversightFiltersType) => void;
  sites: Site[];
  protocols: Protocol[];
}

const REVIEW_STATUS_OPTIONS: { value: ReviewStatus | ''; label: string }[] = [
  { value: '', label: 'All Statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'reviewed', label: 'Reviewed' },
  { value: 'flagged', label: 'Flagged' },
  { value: 'escalated', label: 'Escalated' },
];

export function OversightFilters({
  filters,
  onFilterChange,
  sites,
  protocols,
}: OversightFiltersProps) {
  const [expanded, setExpanded] = useState(true);
  const [searchInput, setSearchInput] = useState(filters.search || '');

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onFilterChange({ ...filters, search: searchInput || undefined, page: 1 });
  };

  const handleClearSearch = () => {
    setSearchInput('');
    onFilterChange({ ...filters, search: undefined, page: 1 });
  };

  const handleFilterChange = (key: keyof OversightFiltersType, value: string | boolean | undefined) => {
    const newFilters = { ...filters, [key]: value || undefined, page: 1 };
    onFilterChange(newFilters);
  };

  const hasActiveFilters =
    filters.site_id ||
    filters.protocol_id ||
    filters.review_status ||
    filters.has_alerts !== undefined ||
    filters.date_from ||
    filters.date_to ||
    filters.search;

  const clearAllFilters = () => {
    setSearchInput('');
    onFilterChange({ page: 1, per_page: filters.per_page });
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden dark:bg-gray-800 dark:border-gray-700">
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:hover:bg-gray-700/50"
      >
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-900 dark:text-gray-100">Filters</span>
          {hasActiveFilters && (
            <span className="px-2 py-0.5 text-xs font-medium bg-primary-100 text-primary-700 rounded-full dark:bg-primary-900/40 dark:text-primary-300">
              Active
            </span>
          )}
        </div>
        {expanded ? (
          <ChevronUp size={20} className="text-gray-500" />
        ) : (
          <ChevronDown size={20} className="text-gray-500" />
        )}
      </button>

      {/* Filter Content */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-gray-200 pt-4 dark:border-gray-700">
          {/* Search Bar */}
          <form onSubmit={handleSearchSubmit} className="mb-4">
            <div className="relative">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search message content..."
                className="w-full rounded-lg border border-gray-300 bg-white py-1.5 pl-10 pr-10 text-sm text-gray-900 placeholder-gray-400 transition-colors duration-150 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400"
              />
              {searchInput && (
                <button
                  type="button"
                  onClick={handleClearSearch}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 transition-colors duration-150 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X size={18} />
                </button>
              )}
            </div>
          </form>

          {/* Filter Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4">
            {/* Site Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                Site
              </label>
              <select
                value={filters.site_id || ''}
                onChange={(e) => handleFilterChange('site_id', e.target.value)}
                className={`w-full ${fieldCls}`}
              >
                <option value="">All Sites</option>
                {sites.map((site) => (
                  <option key={site.id} value={site.id}>
                    {site.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Protocol Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                Protocol
              </label>
              <select
                value={filters.protocol_id || ''}
                onChange={(e) => handleFilterChange('protocol_id', e.target.value)}
                className={`w-full ${fieldCls}`}
              >
                <option value="">All Protocols</option>
                {protocols.map((protocol) => (
                  <option key={protocol.id} value={protocol.id}>
                    {protocol.protocol_number}
                  </option>
                ))}
              </select>
            </div>

            {/* Review Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                Review Status
              </label>
              <select
                value={filters.review_status || ''}
                onChange={(e) => handleFilterChange('review_status', e.target.value as ReviewStatus)}
                className={`w-full ${fieldCls}`}
              >
                {REVIEW_STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Has Alerts Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                Alerts
              </label>
              <select
                value={filters.has_alerts === undefined ? '' : filters.has_alerts ? 'true' : 'false'}
                onChange={(e) =>
                  handleFilterChange(
                    'has_alerts',
                    e.target.value === '' ? undefined : e.target.value === 'true'
                  )
                }
                className={`w-full ${fieldCls}`}
              >
                <option value="">All</option>
                <option value="true">Has Alerts</option>
                <option value="false">No Alerts</option>
              </select>
            </div>

            {/* Date From */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                From Date
              </label>
              <input
                type="date"
                value={filters.date_from?.split('T')[0] || ''}
                onChange={(e) =>
                  handleFilterChange('date_from', e.target.value ? `${e.target.value}T00:00:00Z` : undefined)
                }
                className={`w-full ${fieldCls}`}
              />
            </div>

            {/* Date To */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                To Date
              </label>
              <input
                type="date"
                value={filters.date_to?.split('T')[0] || ''}
                onChange={(e) =>
                  handleFilterChange('date_to', e.target.value ? `${e.target.value}T23:59:59Z` : undefined)
                }
                className={`w-full ${fieldCls}`}
              />
            </div>
          </div>

          {/* Quick Date Filters */}
          <div className="mt-4 flex items-center gap-2">
            <span className="text-sm text-gray-500 dark:text-gray-400">Quick filters:</span>
            <button
              type="button"
              onClick={() => {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                onFilterChange({
                  ...filters,
                  date_from: today.toISOString(),
                  date_to: new Date().toISOString(),
                  page: 1,
                });
              }}
              className="px-3 py-1 text-sm font-medium rounded-full border border-gray-300 text-gray-600 transition-colors duration-150 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              Today
            </button>
            <button
              type="button"
              onClick={() => {
                const now = new Date();
                const sevenDaysAgo = new Date();
                sevenDaysAgo.setDate(now.getDate() - 7);
                sevenDaysAgo.setHours(0, 0, 0, 0);
                onFilterChange({
                  ...filters,
                  date_from: sevenDaysAgo.toISOString(),
                  date_to: now.toISOString(),
                  page: 1,
                });
              }}
              className="px-3 py-1 text-sm font-medium rounded-full border border-gray-300 text-gray-600 transition-colors duration-150 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              Last 7 Days
            </button>
            <button
              type="button"
              onClick={() => {
                const now = new Date();
                const thirtyDaysAgo = new Date();
                thirtyDaysAgo.setDate(now.getDate() - 30);
                thirtyDaysAgo.setHours(0, 0, 0, 0);
                onFilterChange({
                  ...filters,
                  date_from: thirtyDaysAgo.toISOString(),
                  date_to: now.toISOString(),
                  page: 1,
                });
              }}
              className="px-3 py-1 text-sm font-medium rounded-full border border-gray-300 text-gray-600 transition-colors duration-150 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              Last 30 Days
            </button>
          </div>

          {/* Clear Filters Button */}
          {hasActiveFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={clearAllFilters}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium dark:text-primary-400 dark:hover:text-primary-300"
              >
                Clear all filters
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
